import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, GraduationCap, Briefcase, Languages, Target, Share2, Heart, ChevronLeft, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useDataSaver } from "@/context/DataSaverContext";
import { useAuth } from "@/context/AuthContext";
import VoteButton from "@/components/VoteButton";

export default function CandidateProfile() {
  const { slug } = useParams();
  const { dataSaver } = useDataSaver();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [votes, setVotes] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/candidates/${slug}`).then((r) => { setC(r.data); setVotes(r.data.votes); })
      .catch(() => setNotFound(true));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: c.name, url }); return; } catch {} }
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied!");
  };

  const favorite = async () => {
    if (!user) { toast.error("Sign in to save favorites"); return; }
    const { data } = await api.post(`/me/favorites/${c.id}`);
    toast.success(data.added ? "Added to favorites" : "Removed from favorites");
  };

  if (notFound) return <div className="section py-32 text-center"><h1 className="text-3xl text-amber-50">Candidate not found</h1><Link to="/candidates" className="mt-4 inline-block text-gold">← Back to directory</Link></div>;
  if (!c) return <div className="section py-32 text-center text-muted-foreground">Loading…</div>;

  const Info = ({ Icon, label, value }) => value ? (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 text-gold/70" />
      <div><p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</p><p className="text-sm text-amber-50">{value}</p></div>
    </div>
  ) : null;

  return (
    <div data-testid="candidate-profile">
      <div className="section pt-6">
        <Link to="/candidates" className="inline-flex items-center gap-1 text-sm text-gold hover:gap-2 transition-all"><ChevronLeft className="h-4 w-4" /> Directory</Link>
      </div>

      <div className="section grid gap-10 py-8 lg:grid-cols-2">
        <div className="relative">
          <div className="card-lux overflow-hidden">
            <div className="aspect-[3/4] bg-obsidian-600">
              <img src={c.portrait_url} alt={c.name} className="h-full w-full object-cover object-top" loading="eager" />
            </div>
          </div>
          <span className="absolute left-4 top-4 flex items-center gap-2 rounded-full glass-strong px-3 py-1.5">
            <Trophy className="h-4 w-4 text-gold" /><span className="font-mono text-sm gold-text">Rank #{c.rank}</span>
          </span>
        </div>

        <div>
          <p className="kicker mb-2 flex items-center gap-2"><span className="text-2xl">{c.flag}</span> {c.delegation_name}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-none">{c.name}</h1>
          <p className="mt-3 font-display text-lg text-gold">{c.advocacy} Advocate · {c.stage}</p>

          <div className="mt-6 flex items-end gap-6">
            <div>
              <p className="font-mono text-3xl gold-text tabular-nums" data-testid="profile-votes">{votes.toLocaleString()}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Votes</p>
            </div>
            {c.age && <div><p className="font-mono text-3xl text-amber-50 tabular-nums">{c.age}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Age</p></div>}
            {c.page_views != null && <div><p className="font-mono text-3xl text-amber-50 tabular-nums">{Number(c.page_views).toLocaleString()}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Views</p></div>}
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <VoteButton candidate={c} size="lg" onVoted={setVotes} />
            <Link to="/support" className="btn-ghost-gold inline-flex items-center gap-2"><Heart className="h-4 w-4" /> Support</Link>
            <button onClick={favorite} data-testid="favorite-btn" className="btn-ghost-gold inline-flex items-center gap-2"><Heart className="h-4 w-4" /> Favorite</button>
            <button onClick={share} data-testid="share-btn" className="rounded-full border border-gold/40 p-3 text-gold hover:bg-gold/10"><Share2 className="h-4 w-4" /></button>
          </div>

          <p className="mt-8 text-amber-100/70 leading-relaxed">{c.bio}</p>

          <div className="mt-8 grid grid-cols-2 gap-5">
            <Info Icon={MapPin} label="Region / City" value={`${c.city}, ${c.region} Africa`} />
            <Info Icon={GraduationCap} label="Education" value={c.education} />
            <Info Icon={Briefcase} label="Profession" value={c.profession} />
            <Info Icon={Languages} label="Languages" value={(c.languages || []).join(", ")} />
            <Info Icon={Target} label="Goal" value={c.goals} />
          </div>
        </div>
      </div>

      {c.story && (
        <section className="relative overflow-hidden border-y border-gold/15 bg-obsidian-800 py-16">
          <div className="section max-w-3xl text-center">
            <p className="kicker mb-4">Candidate Story</p>
            <p className="font-serif text-2xl sm:text-3xl leading-snug text-amber-50">“{c.quote}”</p>
            <p className="mt-6 text-amber-100/60 leading-relaxed">{c.story}</p>
          </div>
        </section>
      )}

      {c.gallery?.length > 0 && !dataSaver && (
        <section className="section py-16">
          <p className="kicker mb-6">Gallery</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[c.portrait_url, ...c.gallery].map((g, i) => (
              <div key={i} className="card-lux aspect-[3/4] overflow-hidden">
                <img src={g} alt={`${c.name} ${i}`} loading="lazy" className="h-full w-full object-cover object-top" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
